import Parse from "parse";
import time from "src/utils/time";

import downloadFile from "src/utils/downloadFile";
import environment, {
  getEnvironmentVariable,
  electronOnly
} from "src/utils/environment";
import debug from "src/modules/debug-logger";
import {
  isVideoEvent,
  isPhotoEvent
} from "src/modules/activity-log/supported-events";

const { dialog } = window.require("@electron/remote");
const fs = window.require("fs")?.promises;

export async function getMonitorings({ skip, limit = 20 }) {
  const parseSession = await Parse.Session.current();
  const sessionToken = parseSession.attributes.sessionToken;

  const response = await fetch(
    `${getEnvironmentVariable(
      "PARSE_SERVER_URL"
    )}/functions/getCompleteActivityLogEntries`,
    {
      body: `{"skip":${skip}, "limit":${limit}}`,
      headers: {
        "x-parse-application-id": environment.appName,
        "x-parse-session-token": sessionToken
      },
      method: "POST"
    }
  );
  const responseToJson = await response.json();

  const toParse = responseToJson.result || responseToJson;
  const result = toParse.map((monitoring) => {
    monitoring.startDate = monitoring.startDate.iso;
    delete monitoring.user;
    return monitoring;
  });
  return result;
}

export function saveActivityLog(monitoring) {
  return Parse.Cloud.run("saveActivityLog", monitoring);
}

export function deleteActivityLog(activityLogId) {
  return Parse.Cloud.run("deleteActivityLog", { activityLogId });
}

export function deleteActivityLogEvent({ activityLogId, eventId }) {
  return Parse.Cloud.run("deleteActivityLogEvent", { activityLogId, eventId });
}

export async function cleanUpActivityLogEventFiles({ eventType, fileName }) {
  return Parse.Cloud.run("cleanUpActivityLogEventFiles", {
    eventType,
    fileName
  });
}

export async function getSignedUrl(fileName) {
  const { signedUrl } = await Parse.Cloud.run("getSignedUrl", { fileName });
  return signedUrl;
}

export async function getActivityLogFileUrl({ fileName, fileExtension }) {
  const response = await Parse.Cloud.run("getActivityLogFileUrl", {
    fileName,
    fileExtension
  });
  return response;
}

export async function getEventThumbnailDataUrl(
  fileName,
  fileExtension = "jpg"
) {
  const { signedUrl } = await getActivityLogFileUrl({
    fileName: fileName + ".thumbnail",
    fileExtension
  });
  const { dataUrl } = await getDataForMediaUrl(signedUrl);
  return dataUrl;
}

export async function getSnapshotSignedUrl(fileName) {
  const { signedUrl } = await getActivityLogFileUrl({
    fileName: fileName,
    fileExtension: "jpg"
  });
  return signedUrl;
}

export async function getVideoSignedUrl(fileName) {
  const { signedUrl } = await getActivityLogFileUrl({
    fileName: fileName,
    fileExtension: "mp4"
  });
  return signedUrl;
}

export async function getDataForMediaUrl(mediaUrl) {
  const response = await fetch(mediaUrl);
  if (!response.ok) throw new Error("Network response was not ok.");
  const imageBlob = await response.blob();
  const dataUrl = URL.createObjectURL(imageBlob);

  return {
    blob: imageBlob,
    dataUrl
  };
}

export async function downloadEventMedia({
  eventType,
  fileName,
  mediaBlob,
  captureDate
}) {
  const appName = environment.appName;

  let mediaUrl;
  if (isPhotoEvent(eventType)) {
    mediaUrl = await getSnapshotSignedUrl(fileName);
  } else if (isVideoEvent(eventType)) {
    mediaUrl = await getVideoSignedUrl(fileName);
  }

  await electronOnly(async () => {
    const extension = isVideoEvent(eventType) ? "mp4" : "jpg";
    const { filePath } = await dialog.showSaveDialog(null, {
      defaultPath: `${appName}_${time(captureDate).format(
        "YYYY-MM-DD_HH-mm-ss"
      )}.${extension}`
    });

    if (!filePath) return;

    if (mediaBlob) {
      debug.activityLog("Saving from local data");
      const arrayBuffer = await mediaBlob.arrayBuffer();
      return fs.writeFile(filePath, new Uint8Array(arrayBuffer));
    }

    return downloadFile(mediaUrl, filePath);
  });
  if (environment.isBrowser) {
    window.location.href = mediaUrl;
  }
}

export async function getXmppServer() {
  const response = await Parse.Cloud.run("getXmppServer");
  if (!response) return null;
  return response.storageId;
}

export async function enableMonitoringSharing(activityLogId) {
  const response = await Parse.Cloud.run("enableActivityLogSharing", {
    activityLogId
  });
  if (!response) return null;
  return response.toJSON();
}

export async function disableMonitoringSharing(activityLogId) {
  const response = await Parse.Cloud.run("disableActivityLogSharing", {
    activityLogId
  });
  if (!response) return null;
  return response.toJSON();
}
